import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft, Loader2, Check, X, Badge as BadgeIcon, ScanFace, Tag, RadioTower,
  Fingerprint, Car, RotateCcw, CheckCheck, User, UserMinus, Ban, ArrowLeftRight,
  ArrowRightLeft,
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import {
  solicitacoesService,
  Solicitacao,
  SolicitacaoPerson,
  SolicitacaoStatus,
  SolicitacaoType,
  SolicitacaoUnit,
  SOLICITACAO_TYPE_LABELS,
  vehicleTransferSummary,
  SOLICITACAO_STATUS_LABELS,
} from '../../../services/solicitacoesService';
import { makeAbsolutePhotoUrl } from '../../../services/api';
import toast from 'react-hot-toast';
import { CondoType, formatUnitLabel } from '../../../utils/terminology';

interface SolicitacoesPanelProps {
  customerId: string;
  onBack?: () => void;
  lightTheme?: boolean;
  condoType?: CondoType;
  /** Navigate to the requester's unit — when omitted, the unit label isn't clickable. */
  onOpenUnit?: (unitId: string, unitLabel: string, blockId?: string) => void;
  /** Open the requester's profile — that's where the credential actually gets enrolled. */
  onOpenPerson?: (personId: string) => void;
  /**
   * Drop the panel's own "SOLICITAÇÕES" bar (title + its refresh button).
   *
   * It belongs in the Operational View, where this panel IS the view. Embedded in the
   * Solicitações page it repeats the page title and carries a refresh that covers only half the
   * screen — the page owns both.
   */
  hideHeader?: boolean;
  /** Bumped by a host's own refresh control to force a reload. */
  reloadToken?: number;
  /** Lets the host render the spinner in ITS chrome while this panel is fetching. */
  onLoadingChange?: (loading: boolean) => void;
}

/** Only the transitions a síndico can actually make from each status, in the order offered. */
const NEXT_STATUSES: Record<SolicitacaoStatus, SolicitacaoStatus[]> = {
  pending: ['approved', 'rejected'],
  approved: ['completed', 'rejected'],
  rejected: ['pending'],
  // Terminal: the credential exists. Reopening would say nothing about the credential itself.
  completed: [],
  // Also terminal, and not this screen's to undo: the resident withdrew it. "Reabrir" would put a
  // request back in the queue against the wishes of the person who filed it.
  cancelled: [],
};

// Keyed by the status a button MOVES a request to. Partial because `cancelled` is never a target:
// only the resident's own app can cancel, so this screen has no button that produces one.
const ACTION_LABELS: Partial<Record<SolicitacaoStatus, string>> = {
  approved: 'Aprovar',
  rejected: 'Rejeitar',
  completed: 'Concluir',
  pending: 'Reabrir',
};

const TYPE_ICONS: Record<SolicitacaoType, React.ElementType> = {
  facial: ScanFace,
  tag: Tag,
  controle: RadioTower,
  biometria: Fingerprint,
  remover_pessoa: UserMinus,
  remover_veiculo: Ban,
  // The two transfers get different arrows: one moves a credential between cars, the other moves
  // the car between homes.
  transferir_tag: ArrowLeftRight,
  transferir_veiculo: ArrowRightLeft,
};

const FILTERS: Array<{ key: 'all' | SolicitacaoStatus; label: string }> = [
  { key: 'pending', label: 'Pendentes' },
  { key: 'approved', label: 'Aprovadas' },
  { key: 'completed', label: 'Concluídas' },
  { key: 'rejected', label: 'Rejeitadas' },
  { key: 'all', label: 'Todas' },
];

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/** "há 3 dias" — how long the resident has been waiting, the thing a queue is actually judged on. */
function waitingFor(dateStr: string): string | null {
  const then = new Date(dateStr).getTime();
  if (Number.isNaN(then)) return null;
  const minutes = Math.floor((Date.now() - then) / 60000);
  if (minutes < 1) return 'agora mesmo';
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours}h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'há 1 dia' : `há ${days} dias`;
}

function unitLabelOf(unit: SolicitacaoUnit, condoType?: CondoType): string {
  return formatUnitLabel(
    { number: unit.number, blockName: unit.block?.name, streetName: unit.street?.name, streetNumber: unit.street_number },
    condoType
  );
}

function personName(item: Solicitacao): string {
  if (!item.person) return 'Morador';
  return `${item.person.first_name} ${item.person.last_name}`.trim() || 'Morador';
}

function PersonAvatar({ person, size = 36 }: { person?: SolicitacaoPerson; size?: number }) {
  const photoUrl = person?.identity_photo_url || person?.profile_picture_url;
  const absoluteUrl = photoUrl ? makeAbsolutePhotoUrl(photoUrl) : null;
  const initials = person
    ? `${(person.first_name || '')[0] || ''}${(person.last_name || '')[0] || ''}`.toUpperCase()
    : '';

  if (absoluteUrl) {
    return (
      <img
        src={absoluteUrl}
        alt={initials}
        className="rounded-full object-cover shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-blue-600 flex items-center justify-center shrink-0 text-white font-medium"
      style={{ width: size, height: size, fontSize: size * 0.35 }}
    >
      {initials || <User size={size * 0.5} />}
    </div>
  );
}

const SolicitacoesPanel: React.FC<SolicitacoesPanelProps> = ({
  customerId, onBack, lightTheme = false, condoType, onOpenUnit, onOpenPerson,
  hideHeader = false, reloadToken = 0, onLoadingChange,
}) => {
  const [items, setItems] = useState<Solicitacao[]>([]);
  const [loading, setLoading] = useState(true);
  // Pendentes by default: this screen is a work queue, and the open items are the work.
  const [statusFilter, setStatusFilter] = useState<'all' | SolicitacaoStatus>('pending');
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  // Always fetched unfiltered, then filtered client-side, so the tab counts are real counts
  // instead of "however many the current tab returned".
  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await solicitacoesService.listSolicitacoes(customerId));
    } catch {
      toast.error('Erro ao carregar solicitações');
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load, reloadToken]);

  useEffect(() => { onLoadingChange?.(loading); }, [loading, onLoadingChange]);

  const counts = useMemo(() => {
    const byStatus: Record<string, number> = { all: items.length };
    for (const item of items) byStatus[item.status] = (byStatus[item.status] || 0) + 1;
    return byStatus;
  }, [items]);

  const visible = useMemo(
    () => (statusFilter === 'all' ? items : items.filter(i => i.status === statusFilter)),
    [items, statusFilter]
  );

  const handleStatusChange = async (item: Solicitacao, next: SolicitacaoStatus) => {
    setUpdatingId(item.id);
    try {
      await solicitacoesService.updateStatus(customerId, item.id, next);
      // Patched in place rather than refetched: the row usually leaves the current tab, and a full
      // reload would also throw away the scroll position mid-triage.
      setItems(prev => prev.map(i => (i.id === item.id ? { ...i, status: next } : i)));
      toast.success(`Solicitação ${(SOLICITACAO_STATUS_LABELS[next] || next).toLowerCase()}`);
    } catch {
      toast.error('Erro ao atualizar a solicitação');
    } finally {
      setUpdatingId(null);
    }
  };

  // ─── Styles (same tokens as the sibling panels) ───────────────────────────
  const bg = lightTheme ? 'bg-white' : 'bg-gray-950';
  const border = lightTheme ? 'border-gray-200' : 'border-gray-800';
  const text = lightTheme ? 'text-gray-900' : 'text-gray-100';
  const subtext = lightTheme ? 'text-gray-500' : 'text-gray-400';
  const cardBg = lightTheme ? 'bg-gray-50 border-gray-200' : 'bg-gray-900 border-gray-700';

  const statusBadge = (status: SolicitacaoStatus) => {
    switch (status) {
      case 'pending':
        return lightTheme
          ? 'bg-amber-100 text-amber-700 border border-amber-300'
          : 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
      case 'approved':
        return lightTheme
          ? 'bg-blue-100 text-blue-700 border border-blue-300'
          : 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
      case 'completed':
        return lightTheme
          ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
          : 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
      case 'rejected':
        return lightTheme
          ? 'bg-red-100 text-red-700 border border-red-300'
          : 'bg-red-500/20 text-red-400 border border-red-500/30';
      // Slate, not red: withdrawn is an ENDED request, not a refusal.
      case 'cancelled':
        return lightTheme
          ? 'bg-slate-100 text-slate-600 border border-slate-300'
          : 'bg-slate-500/20 text-slate-300 border border-slate-500/30';
    }
  };

  const actionClass = (next: SolicitacaoStatus) => {
    if (next === 'rejected') {
      return lightTheme
        ? 'bg-white border-red-300 text-red-600 hover:bg-red-50'
        : 'bg-gray-900 border-red-600/50 text-red-400 hover:bg-red-900/30';
    }
    if (next === 'pending') {
      return lightTheme
        ? 'bg-white border-gray-300 text-gray-600 hover:bg-gray-100'
        : 'bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-800';
    }
    // approved / completed — the forward move, so it's the filled button.
    return next === 'completed'
      ? 'bg-emerald-600 hover:bg-emerald-700 border-emerald-600 text-white'
      : 'bg-blue-600 hover:bg-blue-700 border-blue-600 text-white';
  };

  const ActionIcon = (next: SolicitacaoStatus) => {
    if (next === 'rejected') return X;
    if (next === 'pending') return RotateCcw;
    if (next === 'completed') return CheckCheck;
    return Check;
  };

  return (
    <div className={cn('flex flex-col h-full', bg)}>
      {/* Header — omitted when a host supplies its own (see hideHeader). */}
      {!hideHeader && (
      <div className={cn('flex items-center gap-3 px-4 py-3 border-b shrink-0', border)}>
        {onBack && (
          <button
            onClick={onBack}
            className={cn('p-1.5 rounded-lg transition-colors', lightTheme ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-gray-800 text-gray-400')}
          >
            <ArrowLeft size={18} />
          </button>
        )}
        <BadgeIcon className={cn('h-4 w-4', subtext)} />
        <h2 className={cn('font-semibold text-base uppercase tracking-wide', text)}>Solicitações</h2>
        <div className="flex-1" />
        <button
          onClick={load}
          disabled={loading}
          className={cn(
            'px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors disabled:opacity-50',
            lightTheme
              ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              : 'bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-800'
          )}
        >
          {loading ? <Loader2 size={13} className="animate-spin" /> : 'Atualizar'}
        </button>
      </div>
      )}

      {/* Filter tabs, each with its own count */}
      <div className={cn('flex flex-wrap gap-1 px-4 py-2 border-b shrink-0', border)}>
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={cn(
              'px-3 py-1 text-xs rounded-full font-medium transition-colors',
              statusFilter === f.key
                ? 'bg-blue-600 text-white'
                : lightTheme ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            )}
          >
            {f.label}
            {(counts[f.key] || 0) > 0 && (
              <span className={cn('ml-1.5 font-bold', statusFilter === f.key ? 'text-white' : lightTheme ? 'text-gray-900' : 'text-gray-200')}>
                {counts[f.key]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-4 py-3 space-y-2">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 size={22} className={cn('animate-spin', subtext)} />
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 gap-2">
            <BadgeIcon className={cn('h-7 w-7', subtext)} />
            <p className={cn('text-sm', subtext)}>
              {statusFilter === 'pending'
                ? 'Nenhuma solicitação pendente.'
                : 'Nenhuma solicitação neste filtro.'}
            </p>
          </div>
        ) : (
          visible.map(item => {
            const TypeIcon = TYPE_ICONS[item.type] || BadgeIcon;
            const waited = item.status === 'pending' ? waitingFor(item.created_at) : null;
            return (
              <div key={item.id} className={cn('rounded-lg border p-3', cardBg)}>
                <div className="flex items-start gap-3">
                  <PersonAvatar person={item.person} />

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        type="button"
                        onClick={() => onOpenPerson?.(item.person_id)}
                        disabled={!onOpenPerson}
                        className={cn('text-sm font-semibold truncate text-left', text, onOpenPerson && 'hover:underline')}
                      >
                        {personName(item)}
                      </button>
                      <span className={cn('text-[11px] px-2 py-0.5 rounded-full font-medium', statusBadge(item.status))}>
                        {SOLICITACAO_STATUS_LABELS[item.status] || item.status}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap mt-1">
                      <span className={cn('inline-flex items-center gap-1 text-xs font-medium', text)}>
                        <TypeIcon size={13} className={subtext} />
                        {SOLICITACAO_TYPE_LABELS[item.type] || item.type}
                      </span>
                      {item.unit && (
                        <button
                          type="button"
                          onClick={() => onOpenUnit?.(item.unit!.id, unitLabelOf(item.unit!, condoType), item.unit!.block?.id)}
                          disabled={!onOpenUnit}
                          className={cn(
                            'text-xs font-medium',
                            lightTheme ? 'text-blue-600' : 'text-blue-400',
                            onOpenUnit && 'hover:underline'
                          )}
                        >
                          {unitLabelOf(item.unit, condoType)}
                        </button>
                      )}
                      {item.vehicle && (
                        <span className={cn('inline-flex items-center gap-1 text-xs', subtext)}>
                          <Car size={13} />
                          {item.vehicle.plate}
                          {item.vehicle.model ? ` · ${item.vehicle.model}` : ''}
                        </span>
                      )}
                      {/* The whole decision on a transfer is WHERE the car goes; a row naming
                          only the plate would send the síndico to the vehicle's record. */}
                      {vehicleTransferSummary(item) && (
                        <span className={cn('inline-flex items-center gap-1 text-xs font-semibold', text)}>
                          {vehicleTransferSummary(item)}
                        </span>
                      )}
                    </div>

                    {item.notes && (
                      <p className={cn('text-xs mt-1.5 whitespace-pre-wrap leading-relaxed', subtext)}>
                        {item.notes}
                      </p>
                    )}

                    {/* What the resident said instead of the request. Required of them precisely
                        so a withdrawn request is never an unexplained gap in this queue. */}
                    {item.cancellation_reason && (
                      <p className={cn('text-xs mt-1.5 whitespace-pre-wrap leading-relaxed', subtext)}>
                        Cancelada pelo morador: {item.cancellation_reason}
                      </p>
                    )}

                    <p className={cn('text-[11px] mt-1.5', subtext)}>
                      {formatDate(item.created_at)}
                      {waited && ` · aguardando ${waited}`}
                    </p>
                  </div>

                  {/* Actions available from this status */}
                  <div className="flex flex-col gap-1.5 shrink-0">
                    {(NEXT_STATUSES[item.status] || []).map(next => {
                      const Icon = ActionIcon(next);
                      return (
                        <button
                          key={next}
                          onClick={() => handleStatusChange(item, next)}
                          disabled={updatingId === item.id}
                          className={cn(
                            'flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors disabled:opacity-50',
                            actionClass(next)
                          )}
                        >
                          {updatingId === item.id
                            ? <Loader2 size={12} className="animate-spin" />
                            : <Icon size={12} />}
                          {ACTION_LABELS[next] ?? next}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SolicitacoesPanel;
